import React, { useState } from 'react';
import { Button, Col, Modal, ModalBody, ModalHeader, Input, Spinner, Label } from 'reactstrap';

interface CarTypeForm {
  name: string;
  passengers: string;
  infants: string;
  babySeats: string;
  luggages: string;
}
interface SubmissionForm {
  id: string;
  name: string;
  passengers: string;
  infants: string;
  babySeats: string;
  luggages: string;
  tag: string;
}

type CarTypeErrors = Partial<Record<keyof CarTypeForm, string>>;

interface AddCarTypeProps {
  onClose: () => void;
  handleAddCarType: (form: SubmissionForm) => void;
}

const AddCarType: React.FC<AddCarTypeProps> = ({ onClose, handleAddCarType }) => {
  const [formData, setFormData] = useState<CarTypeForm>({
    name: '',
    passengers: '',
    infants: '',
    babySeats: '',
    luggages: '',
  });

  const [errors, setErrors] = useState<CarTypeErrors>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;

    // Prevent negative number typing in UI
    if (e.target.type === 'number' && Number(value) < 0) return;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    setErrors(prev => ({
      ...prev,
      [name]: '',
    }));
  };

  const validate = (): boolean => {
    const newErrors: CarTypeErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Car name is required';
    }

    const numericFields: (keyof CarTypeForm)[] = ['passengers', 'infants', 'babySeats', 'luggages'];

    numericFields.forEach(field => {
      const value = formData[field];
      const num = Number(value);

      if (value === '') {
        newErrors[field] = 'This field is required';
      } else if (isNaN(num) || num < 0) {
        newErrors[field] = 'Must be a non-negative number';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (): void => {
    if (!validate()) return;
    handleAddCarType({ ...formData, id: Math.random().toString(36).substring(2, 10), tag: 'partially_configured' });
  };

  return (
    <Modal isOpen={true} size="md">
      <ModalHeader toggle={onClose}>
        <h4>Add Car Type</h4>
      </ModalHeader>

      <ModalBody>
        <Col md={12} className="mb-3">
          <Label>Car Name</Label>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter car name"
            className={errors.name ? 'is-invalid' : ''}
          />
          {errors.name && <div className="text-danger">{errors.name}</div>}
        </Col>

        <Col md={12} className="mb-3">
          <Label>No. of Passengers</Label>
          <Input
            type="number"
            name="passengers"
            value={formData.passengers}
            onChange={handleChange}
            placeholder="Enter number"
            min="0"
            className={errors.passengers ? 'is-invalid' : ''}
          />
          {errors.passengers && <div className="text-danger">{errors.passengers}</div>}
        </Col>

        <Col md={12} className="mb-3">
          <Label>No. of Infants</Label>
          <Input
            type="number"
            name="infants"
            value={formData.infants}
            onChange={handleChange}
            placeholder="Enter number"
            min="0"
            className={errors.infants ? 'is-invalid' : ''}
          />
          {errors.infants && <div className="text-danger">{errors.infants}</div>}
        </Col>

        <Col md={12} className="mb-3">
          <Label>No. of Baby Seats</Label>
          <Input
            type="number"
            name="babySeats"
            value={formData.babySeats}
            onChange={handleChange}
            placeholder="Enter number"
            min="0"
            className={errors.babySeats ? 'is-invalid' : ''}
          />
          {errors.babySeats && <div className="text-danger">{errors.babySeats}</div>}
        </Col>

        <Col md={12} className="mb-4">
          <Label>No. of Luggages</Label>
          <Input
            type="number"
            name="luggages"
            value={formData.luggages}
            onChange={handleChange}
            placeholder="Enter number"
            min="0"
            className={errors.luggages ? 'is-invalid' : ''}
          />
          {errors.luggages && <div className="text-danger">{errors.luggages}</div>}
        </Col>

        <Col md={12} className="text-center">
          <Button color="primary" style={{ fontSize: '14px', width: '200px' }} onClick={handleSubmit}>
            Add Car Type
          </Button>
        </Col>
      </ModalBody>
    </Modal>
  );
};

export default AddCarType;
